// Test & Refine redesign - Configure view.
//
// The two-panel body: a "Shape the test" plan on the left (~40%) joined to
// the "Simulation queue" on the right (~60%) by an AI intro band, plus a
// run bar that is sticky on desktop and static below 860px. All data and
// handlers arrive as props from the orchestrator; only view-only UI state
// (search text, expanded row, accordion open) lives here.
import { useMemo, useState, type CSSProperties } from "react";
import {
  Search,
  ChevronRight,
  ChevronDown,
  ArrowRight,
  Sliders,
  Check,
  X,
  Play,
  CircleCheck,
  CircleAlert,
} from "lucide-react";

import { SimulationUploadZone } from "../simulation-upload-zone";
import type { SimulationQueueItem, SandboxSuggestion } from "@/app/services/simulation/ai-sandbox-service";
import type { TestRefineShellProps } from "./test-refine-shell";
import {
  TrOrb,
  Pill,
  priorityConfig,
  effortColor,
  rowAccent,
  clampPctWidth,
} from "./shared";

type ConfigureViewProps = TestRefineShellProps & { padding: string };

// NOTE: there is deliberately no pre-run numeric prediction here. Any
// before/after % or lift shown before running would be fabricated, the real
// numbers only exist once the simulation runs (that is what it produces). The
// left panel shows the AI's recommended test (question + actions + why), not
// invented percentages.

export function ConfigureView(props: ConfigureViewProps) {
  const {
    padding,
    sandboxData,
    availableModels,
    selectedModels,
    setSelectedModels,
    onModelChangeContext,
    query,
    setQuery,
    competitors,
    competitorInput,
    setCompetitorInput,
    addCompetitor,
    removeCompetitor,
    selectedQueueItems,
    setSelectedQueueItems,
    selectedFile,
    setSelectedFile,
    onViewContent,
    onRunSimulation,
    suggestions,
    suggestionsLoading,
  } = props;

  const queue = sandboxData.simulationQueue || [];

  const validQueueIds = useMemo(() => new Set(queue.map((a) => Number(a.id))), [queue]);
  const recommendation = suggestions[0];
  const recommendedIds = useMemo(
    () => new Set((recommendation?.actionIds || []).filter((id) => validQueueIds.has(Number(id)))),
    [recommendation, validQueueIds]
  );
  const applySuggestion = (s: SandboxSuggestion) => {
    setQuery(s.question);
    setSelectedQueueItems((s.actionIds || []).map(Number).filter((id) => validQueueIds.has(id)));
  };

  const [actionSearch, setActionSearch] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [adjustOpen, setAdjustOpen] = useState(false);

  const selectedSet = useMemo(() => new Set(selectedQueueItems.map(Number)), [selectedQueueItems]);
  const selCount = selectedQueueItems.length;
  const modelCount = selectedModels.length;

  const filteredQueue = useMemo(() => {
    const q = actionSearch.trim().toLowerCase();
    if (!q) return queue;
    return queue.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        String(a.type || "").toLowerCase().includes(q) ||
        String(a.sourceIssue || "").toLowerCase().includes(q)
    );
  }, [queue, actionSearch]);

  const toggleItem = (id: number) => {
    const next = selectedSet.has(id)
      ? selectedQueueItems.filter((x) => Number(x) !== id)
      : [...selectedQueueItems, id];
    setSelectedQueueItems(next);
  };

  const allSelected = queue.length > 0 && selectedQueueItems.length === queue.length;
  const toggleAll = () => setSelectedQueueItems(allSelected ? [] : queue.map((a) => Number(a.id)));

  const toggleModel = (id: string) => {
    const has = selectedModels.includes(id);
    const next = has ? selectedModels.filter((m) => m !== id) : [...selectedModels, id];
    setSelectedModels(next);
    onModelChangeContext?.(id, !has, next.length);
  };

  const hasQuery = !!query.trim();
  const planReady = hasQuery && selCount > 0 && modelCount > 0;
  const showRecommendation = !!recommendation && !planReady;

  const compSummary =
    competitors.length > 0 ? competitors.join(", ") : "the category leaders it finds";
  const actionsWord = selCount === 1 ? "action" : "actions";

  // Run readiness messaging (mirrors the orchestrator validation gates).
  const ready = hasQuery && modelCount > 0 && selCount > 0;
  const readyLabel = !hasQuery
    ? "Enter a query to run"
    : modelCount === 0
    ? "Select at least one model"
    : selCount === 0
    ? "Select at least one action"
    : `Ready, ${selCount} ${actionsWord} across ${modelCount} models`;

  return (
    <>
      <div style={{ padding: `24px ${padding} 40px` }}>
        <div
          style={{
            border: "1px solid var(--border)",
            borderRadius: 16,
            padding: 24,
            display: "flex",
            gap: 16,
            alignItems: "stretch",
            flexWrap: "wrap",
            background:
              "radial-gradient(130% 130% at 0% 0%, var(--spectral-violet-10), transparent 42%), radial-gradient(130% 130% at 100% 100%, var(--alpha-teal-10), transparent 45%), var(--card)",
          }}
          className="tr-two-panel"
        >
          {/* ── Intro band ─────────────────────────────────────── */}
          <div
            style={{
              flex: "1 1 100%",
              display: "flex",
              alignItems: "flex-start",
              gap: 13,
              paddingBottom: 4,
            }}
          >
            <TrOrb size={36} style={{ marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: ".07em",
                  color: "var(--spectral-violet)",
                  marginBottom: 6,
                }}
              >
                Private sandbox
              </div>
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  letterSpacing: "-.01em",
                  color: "var(--foreground)",
                  margin: "0 0 5px",
                }}
              >
                Try a fix before it goes live.
              </h2>
              <p style={{ fontSize: 14, color: "var(--muted-foreground)", lineHeight: 1.55, margin: 0 }}>
                Pick the fixes you want to test from your queue, set up the question to run them against,
                and Upmode AI estimates how much each model would move. It's a sandbox, so nothing you do
                here reaches your live site.
              </p>
            </div>

            {/* Right slot. The run control used to live in the sticky bottom
                bar, which put it underneath the assistant's closed affordance
                in the reserved bottom-right zone. Height-capped and centred
                against the band rather than stretched to it. */}
            <button
              onClick={onRunSimulation}
              disabled={!ready}
              title={ready ? undefined : readyLabel}
              style={{
                flexShrink: 0,
                alignSelf: "center",
                height: 40,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontSize: 14,
                fontWeight: 600,
                padding: "0 20px",
                borderRadius: 9,
                border: "none",
                cursor: ready ? "pointer" : "not-allowed",
                background: ready ? "var(--orb-gradient)" : "var(--muted)",
                color: ready ? "var(--on-orb)" : "var(--muted-foreground)",
                boxShadow: ready ? "var(--shadow-spectral-violet-glow)" : "none",
              }}
            >
              <Play size={15} strokeWidth={1.8} />
              Run it
            </button>
          </div>

          {/* ── Right: Simulation queue (60%) ──────────────────── */}
          <section
            className="tr-panel-right"
            style={{
              flex: "6 1 420px",
              minWidth: 360,
              order: 2,
              display: "flex",
              flexDirection: "column",
              background: "var(--card)",
              border: "1px solid var(--border)",
              borderRadius: 14,
              boxShadow: "var(--elevation-sm)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "16px 18px",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 600, color: "var(--foreground)" }}>
                Pick what to test
              </span>
              <Pill color="var(--teal-text)" bg="var(--alpha-teal-10)" border="var(--alpha-teal-20)">
                {queue.length} in queue
              </Pill>
              {recommendedIds.size > 0 && (
                <button
                  onClick={() => setSelectedQueueItems([...recommendedIds])}
                  className="tr-chip"
                  style={{
                    marginLeft: "auto",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: "var(--spectral-violet)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 9999,
                      background: "linear-gradient(135deg, var(--spectral-violet), var(--signal-blue))",
                      flexShrink: 0,
                    }}
                  />
                  Select recommended
                </button>
              )}
              <span
                style={{
                  marginLeft: recommendedIds.size > 0 ? undefined : "auto",
                  fontSize: 12,
                  color: "var(--muted-foreground)",
                }}
              >
                <strong
                  style={{ color: "var(--foreground)", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
                >
                  {selCount}
                </strong>{" "}
                selected
              </span>
            </div>

            {/* Search (always shown, ruling #6) */}
            <div
              style={{
                padding: "12px 14px",
                borderBottom: "1px solid var(--border)",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
                <Search
                  size={15}
                  strokeWidth={1.7}
                  style={{
                    position: "absolute",
                    left: 11,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--muted-foreground)",
                  }}
                />
                <input
                  className="tr-input"
                  type="text"
                  value={actionSearch}
                  onChange={(e) => setActionSearch(e.target.value)}
                  placeholder="Search actions by title, theme, source issue..."
                  style={{
                    width: "100%",
                    height: 36,
                    padding: "0 12px 0 34px",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    background: "var(--background)",
                    fontSize: 13,
                    fontFamily: "var(--font-sans)",
                    color: "var(--foreground)",
                    outline: "none",
                  }}
                />
              </div>
              <span style={{ fontSize: 12, color: "var(--muted-foreground)", whiteSpace: "nowrap" }}>
                {filteredQueue.length} / {queue.length} shown
              </span>
            </div>

            {/* Select-all header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 11,
                padding: "9px 18px",
                borderBottom: "1px solid var(--border)",
                background: "var(--muted)",
              }}
            >
              <button
                onClick={toggleAll}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <CheckBox checked={allSelected} />
                <span style={hdrColStyle}>Priority · Action</span>
              </button>
              <span style={{ ...hdrColStyle, marginLeft: "auto" }}>Impact / Effort</span>
            </div>

            {/* Rows */}
            <div className="tr-scroll tr-queue-scroll">
              {filteredQueue.length === 0 ? (
                <div style={{ padding: "28px 18px", textAlign: "center", fontSize: 13, color: "var(--muted-foreground)" }}>
                  {queue.length === 0 ? "No actions in your queue yet." : "No actions match your search."}
                </div>
              ) : (
                filteredQueue.map((a) => (
                  <QueueRow
                    key={a.id}
                    item={a}
                    recommended={recommendedIds.has(Number(a.id))}
                    selected={selectedSet.has(Number(a.id))}
                    expanded={expandedId === Number(a.id)}
                    onToggle={() => toggleItem(Number(a.id))}
                    onExpand={() =>
                      setExpandedId(expandedId === Number(a.id) ? null : Number(a.id))
                    }
                    onViewContent={() => onViewContent(a)}
                  />
                ))
              )}
            </div>
          </section>

          {/* ── Left: Shape the test (40%) ─────────────────────── */}
          <aside
            className="tr-panel-left"
            style={{
              flex: "4 1 320px",
              minWidth: 300,
              order: 1,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            {/* Plan card */}
            <section
              style={{
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 14,
                boxShadow: "var(--elevation-sm)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "16px 18px",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <span style={{ fontSize: 15, fontWeight: 600, color: "var(--foreground)" }}>
                  Shape the test
                </span>
              </div>

              <div style={{ padding: 18 }}>
                <p style={{ fontSize: 15, lineHeight: 1.6, color: "var(--foreground)", margin: "0 0 14px" }}>
                  Test{" "}
                  <strong style={{ fontWeight: 600 }}>
                    {selCount} selected {actionsWord}
                  </strong>{" "}
                  against this buyer question, across <strong style={{ fontWeight: 600 }}>{modelCount} models</strong>,
                  benchmarked against {compSummary}.
                </p>

                <label style={uppercaseLabel}>
                  The user question <span style={{ color: "var(--infrared-coral)" }}>*</span>
                </label>
                <input
                  className="tr-input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter your question"
                  style={{
                    width: "100%",
                    height: 48,
                    padding: "0 15px",
                    border: `1.5px solid ${hasQuery ? "var(--border)" : "var(--spectral-violet-20)"}`,
                    borderRadius: 10,
                    background: "var(--background)",
                    fontSize: 15,
                    fontWeight: 500,
                    fontFamily: "var(--font-sans)",
                    color: "var(--foreground)",
                    outline: "none",
                    marginBottom: 9,
                  }}
                />

                {/* AI-suggested questions to test (real engine), styled to
                    match Brand Monitoring's starter chips: inline label +
                    rounded-full pill chips with the mini gradient dot.
                    Shimmer while Claude thinks; hidden when there are none. */}
                {(suggestionsLoading || suggestions.length > 0) && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                      marginBottom: showRecommendation ? 18 : 4,
                    }}
                  >
                    <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                      {suggestionsLoading ? "Finding tests worth running" : "Not sure what to test?"}
                    </span>
                    {suggestionsLoading
                      ? [0, 1, 2].map((i) => (
                          <span
                            key={i}
                            style={{
                              height: 32,
                              width: 170 - i * 22,
                              borderRadius: 9999,
                              background: "var(--muted)",
                              border: "1px solid var(--border)",
                              animation: "trPulse 1.4s ease-in-out infinite",
                            }}
                          />
                        ))
                      : suggestions.map((s, i) => (
                          <button
                            key={i}
                            className="tr-chip"
                            onClick={() => applySuggestion(s)}
                            title={s.why || undefined}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 7,
                              minHeight: 32,
                              padding: "6px 13px",
                              borderRadius: 9999,
                              background: "var(--card)",
                              border: "1px solid var(--border)",
                              fontSize: 12,
                              fontWeight: 500,
                              color: "var(--foreground)",
                              cursor: "pointer",
                              textAlign: "left",
                              maxWidth: "100%",
                            }}
                          >
                            <span
                              style={{
                                width: 13,
                                height: 13,
                                borderRadius: 9999,
                                background: "linear-gradient(135deg, var(--spectral-violet), var(--signal-blue))",
                                flexShrink: 0,
                              }}
                            />
                            <span>{s.question}</span>
                          </button>
                        ))}
                  </div>
                )}

                {/* Recommended test. The estimate comes from Claude via the
                    suggestion engine, using the same query-specific before/after
                    rules the simulation uses, so it won't contradict the run.
                    Shown only for the AI's recommendation (before the user has
                    configured their own test). Labeled as an estimate. */}
                {showRecommendation && recommendation && (() => {
                  const before = recommendation.predictedBefore;
                  const after = recommendation.predictedAfter;
                  const hasEstimate = before != null && after != null;
                  return (
                    <div
                      style={{
                        border: "1px solid var(--border)",
                        borderRadius: 12,
                        padding: 16,
                        background: "var(--muted)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: hasEstimate ? 12 : 8 }}>
                        <span style={uppercaseLabel}>Suggested test</span>
                        {hasEstimate && (
                          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--signal-green)" }}>
                            +{Math.max(0, (after as number) - (before as number))}pp est.
                          </span>
                        )}
                      </div>

                      {hasEstimate && (
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, color: "var(--muted-foreground)", marginBottom: 4 }}>Now (est.)</div>
                            <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "var(--muted-foreground)", lineHeight: 1 }}>
                              {before}%
                            </div>
                            <Bar width={clampPctWidth(before as number)} track="var(--silver-mist)" fill="var(--muted-foreground)" />
                          </div>
                          <ArrowRight size={18} strokeWidth={1.7} style={{ color: "var(--muted-foreground)", flexShrink: 0, marginTop: 16 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, color: "var(--teal-text)", fontWeight: 600, marginBottom: 4 }}>After these fixes</div>
                            <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "var(--teal-text)", lineHeight: 1 }}>
                              {after}%
                            </div>
                            <Bar width={clampPctWidth(after as number)} track="var(--alpha-teal-10)" fill="var(--alpha-teal)" />
                          </div>
                        </div>
                      )}

                      <p style={{ fontSize: 11.5, color: "var(--muted-foreground)", lineHeight: 1.5, margin: "0 0 9px" }}>
                        <strong style={{ color: "var(--spectral-violet)", fontWeight: 600 }}>Upmode AI suggests</strong>{" "}
                        testing "{recommendation.question}" with {recommendedIds.size}{" "}
                        {recommendedIds.size === 1 ? "action" : "actions"}
                        {recommendation.why ? `, ${recommendation.why}` : ""}.
                        {hasEstimate ? " Estimate only, run it to confirm." : ""}
                      </p>
                      <button
                        onClick={() => applySuggestion(recommendation)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          color: "var(--on-orb)",
                          background: "var(--orb-gradient)",
                          border: "none",
                          borderRadius: 8,
                          padding: "6px 12px",
                          cursor: "pointer",
                        }}
                      >
                        Use this test
                      </button>
                    </div>
                  );
                })()}
              </div>
            </section>

            {/* Adjust setup (collapsed by default) */}
            <section
              style={{
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 14,
                boxShadow: "var(--elevation-sm)",
                overflow: "hidden",
              }}
            >
              <button
                onClick={() => setAdjustOpen((v) => !v)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 11,
                  padding: "15px 18px",
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: "var(--muted)",
                    border: "1px solid var(--border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--muted-foreground)",
                    flexShrink: 0,
                  }}
                >
                  <Sliders size={14} strokeWidth={1.7} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>Adjust setup</div>
                  <div style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
                    {modelCount} models ·{" "}
                    {competitors.length > 0
                      ? `${competitors.length} competitor${competitors.length !== 1 ? "s" : ""}`
                      : "auto competitors"}
                  </div>
                </div>
                <ChevronDown
                  size={16}
                  strokeWidth={2}
                  className="tr-chevron"
                  style={{ color: "var(--muted-foreground)", transform: adjustOpen ? "rotate(180deg)" : "none" }}
                />
              </button>

              {adjustOpen && (
                <div style={{ padding: "0 18px 18px" }} className="tr-fade">
                  {/* Models */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      margin: "6px 0 9px",
                    }}
                  >
                    <label style={{ ...uppercaseLabel, color: "var(--foreground)" }}>
                      AI models <span style={{ color: "var(--muted-foreground)", fontWeight: 500 }}>({modelCount})</span>
                    </label>
                  </div>
                  <div
                    className="tr-grid-2"
                    style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 18 }}
                  >
                    {availableModels.map((m) => {
                      const on = selectedModels.includes(m.id);
                      return (
                        <button
                          key={m.id}
                          onClick={() => !m.comingSoon && toggleModel(m.id)}
                          disabled={m.comingSoon}
                          className="tr-row"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            textAlign: "left",
                            padding: "11px 13px",
                            borderRadius: 10,
                            cursor: m.comingSoon ? "not-allowed" : "pointer",
                            opacity: m.comingSoon ? 0.5 : 1,
                            background: on ? "var(--alpha-teal-5)" : "var(--card)",
                            border: `1px solid ${on ? "var(--alpha-teal-30)" : "var(--border)"}`,
                          }}
                        >
                          <CheckBox checked={on} />
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 12.5,
                                fontWeight: 600,
                                color: "var(--foreground)",
                                lineHeight: 1.2,
                              }}
                            >
                              <span
                                style={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {m.name}
                              </span>
                              {m.recommended && (
                                <span
                                  title="Recommended"
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: 9999,
                                    background: "var(--orb-gradient)",
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </span>
                            <span style={{ display: "block", fontSize: 10.5, color: "var(--muted-foreground)" }}>
                              {m.comingSoon ? "Coming soon" : m.provider}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Competitors */}
                  <label style={{ ...uppercaseLabel, color: "var(--foreground)" }}>Competitor brands</label>
                  <p style={{ fontSize: 11.5, color: "var(--muted-foreground)", margin: "0 0 9px" }}>
                    Optional, up to 5. Leave blank and AI benchmarks against the leaders it finds.
                  </p>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    <input
                      className="tr-input"
                      type="text"
                      value={competitorInput}
                      onChange={(e) => setCompetitorInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addCompetitor();
                        }
                      }}
                      placeholder="Add a competitor brand"
                      disabled={competitors.length >= 5}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: 38,
                        padding: "0 12px",
                        border: "1px solid var(--border)",
                        borderRadius: 8,
                        background: "var(--background)",
                        fontSize: 13,
                        fontFamily: "var(--font-sans)",
                        color: "var(--foreground)",
                        outline: "none",
                      }}
                    />
                    <button
                      onClick={addCompetitor}
                      disabled={!competitorInput.trim() || competitors.length >= 5}
                      style={{
                        padding: "0 14px",
                        borderRadius: 8,
                        background: "var(--muted)",
                        border: "1px solid var(--border)",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--foreground)",
                        cursor:
                          !competitorInput.trim() || competitors.length >= 5 ? "not-allowed" : "pointer",
                        flexShrink: 0,
                      }}
                    >
                      Add
                    </button>
                  </div>
                  {competitors.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 16 }}>
                      {competitors.map((c) => (
                        <span
                          key={c}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: 12,
                            fontWeight: 500,
                            color: "var(--foreground)",
                            background: "var(--signal-blue-10)",
                            border: "1px solid var(--signal-blue-20)",
                            borderRadius: 9999,
                            padding: "4px 11px",
                          }}
                        >
                          {c}
                          <button
                            onClick={() => removeCompetitor(c)}
                            aria-label={`Remove ${c}`}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "var(--muted-foreground)",
                              padding: 0,
                              display: "inline-flex",
                            }}
                          >
                            <X size={12} strokeWidth={2} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Upload zone, existing component kept as-is */}
                  <SimulationUploadZone selectedFile={selectedFile} onFileSelect={setSelectedFile} />
                </div>
              )}
            </section>
          </aside>
        </div>
      </div>

      {/* ── Run bar (sticky on desktop, static below 860px) ─────── */}
      <div
        className="tr-runbar"
        style={{
          position: "sticky",
          bottom: 0,
          zIndex: 20,
          background: "var(--card)",
          borderTop: "1px solid var(--border)",
          padding: `14px ${padding}`,
          display: "flex",
          alignItems: "center",
          gap: 14,
          flexWrap: "wrap",
          boxShadow: "0 -4px 12px -6px rgba(10, 13, 18, 0.1)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--muted-foreground)" }}>
          {ready ? (
            <CircleCheck size={15} strokeWidth={1.8} style={{ color: "var(--signal-green)" }} />
          ) : (
            <CircleAlert size={15} strokeWidth={1.7} style={{ color: "var(--muted-foreground)" }} />
          )}
          <span>{readyLabel}</span>
        </div>
      </div>
    </>
  );
}

// ── Queue row ─────────────────────────────────────────────────────
function QueueRow({
  item,
  recommended,
  selected,
  expanded,
  onToggle,
  onExpand,
  onViewContent,
}: {
  item: SimulationQueueItem;
  recommended: boolean;
  selected: boolean;
  expanded: boolean;
  onToggle: () => void;
  onExpand: () => void;
  onViewContent: () => void;
}) {
  const pri = priorityConfig(item.priority);
  const accent = rowAccent(item.priority, item.type);
  const effort = String(item.effort || "Medium");
  const impactNum = Number(item.impact ?? item.expectedImpact);
  const dateLabel = formatDate(item.lastModified);

  return (
    <div style={{ borderBottom: "1px solid var(--border)", background: selected ? "var(--card)" : "var(--muted)" }}>
      <div className="tr-row" style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 18px" }}>
        <button
          onClick={onToggle}
          aria-label={selected ? "Deselect action" : "Select action"}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}
        >
          <CheckBox checked={selected} large />
        </button>
        <span
          style={{
            width: 3,
            alignSelf: "stretch",
            minHeight: 30,
            borderRadius: 2,
            flexShrink: 0,
            background: accent,
          }}
        />
        <button
          onClick={onExpand}
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: "left",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                borderRadius: 5,
                padding: "1px 7px",
                background: pri.bg,
                color: pri.color,
              }}
            >
              {pri.label}
            </span>
            {recommended && (
              <span
                title="Recommended for the suggested test"
                style={{
                  fontSize: 9.5,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: ".04em",
                  borderRadius: 5,
                  padding: "1px 6px",
                  background: "var(--spectral-violet-10)",
                  color: "var(--spectral-violet)",
                  flexShrink: 0,
                }}
              >
                Suggested
              </span>
            )}
            <span
              style={{
                fontSize: 13.5,
                fontWeight: 600,
                color: "var(--foreground)",
                lineHeight: 1.35,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.title}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              fontSize: 11,
              color: "var(--muted-foreground)",
            }}
          >
            <span>{item.source}</span>
            <Dot />
            <span>{item.type}</span>
            <Dot />
            <span>{dateLabel}</span>
          </div>
        </button>
        <span style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 9 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              fontWeight: 700,
              color: "var(--teal-text)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <ArrowRight size={12} strokeWidth={2} style={{ transform: "rotate(-45deg)" }} />
            {Number.isNaN(impactNum) ? String(item.expectedImpact || "") : impactNum}
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 11,
              fontWeight: 600,
              color: effortColor(effort),
            }}
          >
            <span
              style={{ width: 5, height: 5, borderRadius: 9999, background: effortColor(effort) }}
            />
            {effort}
          </span>
          <button
            onClick={onExpand}
            aria-label={expanded ? "Collapse" : "Expand"}
            className="tr-chevron"
            style={{
              display: "inline-flex",
              color: "var(--muted-foreground)",
              transform: expanded ? "rotate(90deg)" : "none",
              cursor: "pointer",
              background: "none",
              border: "none",
              padding: 0,
            }}
          >
            <ChevronRight size={15} strokeWidth={2} />
          </button>
        </span>
      </div>

      {expanded && (
        <div style={{ padding: "0 18px 15px 51px", display: "flex", flexDirection: "column", gap: 10 }} className="tr-fade">
          {item.description && (
            <p style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.55, margin: 0 }}>
              {item.description}
            </p>
          )}
          {item.sourceIssue && (
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: ".05em",
                  color: "var(--infrared-coral)",
                  marginBottom: 4,
                }}
              >
                Source issue
              </div>
              <p style={{ fontSize: 12.5, color: "var(--foreground)", lineHeight: 1.5, margin: 0 }}>
                {item.sourceIssue}
              </p>
            </div>
          )}
          <div>
            <button
              onClick={onViewContent}
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--teal-text)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              View full details
              <ChevronRight size={14} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Small shared bits (local) ─────────────────────────────────────
const uppercaseLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: ".05em",
  color: "var(--muted-foreground)",
  marginBottom: 7,
};

const hdrColStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: ".05em",
  color: "var(--muted-foreground)",
};

function CheckBox({ checked, large }: { checked: boolean; large?: boolean }) {
  const size = large ? 18 : 16;
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 5,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: checked ? "var(--alpha-teal)" : "var(--background)",
        border: `1px solid ${checked ? "var(--alpha-teal)" : "var(--silver-mist)"}`,
        color: "var(--on-teal-text)",
      }}
    >
      {checked && <Check size={large ? 12 : 11} strokeWidth={3} />}
    </span>
  );
}

function Bar({ width, track, fill }: { width: string; track: string; fill: string }) {
  return (
    <div
      style={{
        height: 6,
        borderRadius: 9999,
        background: track,
        overflow: "hidden",
        marginTop: 7,
      }}
    >
      <div
        style={{
          height: "100%",
          borderRadius: 9999,
          width,
          background: fill,
          transition: "width .6s var(--ease-standard)",
        }}
      />
    </div>
  );
}

function Dot() {
  return <span style={{ width: 3, height: 3, borderRadius: 9999, background: "var(--silver-mist)" }} />;
}

function formatDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
